// ─────────────────────────────────────────────
//  useSheetEngineQuestions — the names on the daily sheet that match nobody.
//
//  ⚠️ THE ENGINE HAS ALWAYS RECORDED THESE AND NOBODY COULD READ THEM. Every
//  cycle, `functions/sheetEngine.js` writes a run document carrying `questions`
//  — each one a cell the identity ladder refused to resolve, kept for a human
//  because the rule is "never create, always ask". Nothing ever asked: the
//  collection was admin-read-only and no screen in ATC opened it.
//
//  On 2026-09-11 "JUS" sat unresolved as the ATTENDING on WOR 11 and WC E1 for
//  a whole morning. The sheet named a doctor, the board showed none, and the
//  document explaining precisely that was sitting in Firestore where the person
//  running the board could not reach it. The owner found it by looking at the
//  sheet and the board side by side.
//
//  ⚠️ A QUESTION IS NOT AN ERROR, AND THAT IS WHY IT NEEDS A SURFACE. The
//  ladder refusing "Gabi S" is it working correctly — an abbreviated name has
//  no single right answer and guessing one puts the wrong person in a room. The
//  failure was never the refusal; it was that the refusal was silent.
//
//  Read-only. `sheet_engine_runs` is unwritable by every client (firestore.rules).
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
// ─────────────────────────────────────────────
import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase/config";

/** Normalise one run's questions into rows a surface can render. */
export function questionRows(run) {
  const list = Array.isArray(run?.questions) ? run.questions : [];
  const out = [];
  const seen = new Set();
  for (const q of list) {
    const raw = String(q?.raw || "").trim();
    const room = String(q?.room || "").trim();
    if (!raw) continue;
    // ⚠️ THE SAME PERSON APPEARS ONCE PER ROOM THEY ARE NAMED IN, and that is
    // worth keeping — "JUS in two rooms" is a bigger hole than "JUS somewhere".
    // Only an exact repeat of the same cell is dropped.
    const key = `${room}|${q?.seat || ""}|${raw.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      raw,
      room,
      // "attending" / "ahp" — which seat the board cannot fill.
      seat: String(q?.seat || "").trim(),
      reason: String(q?.reason || "unresolved").trim(),
    });
  }
  return out;
}

const millisOf = (run) => {
  const t = run?.at;
  if (!t) return 0;
  if (typeof t.toMillis === "function") return t.toMillis();
  if (t instanceof Date) return t.getTime();
  if (typeof t.seconds === "number") return t.seconds * 1000;
  return Date.parse(t) || 0;
};

/**
 * The newest run for this facility and day, and the names it could not match.
 *
 * ⚠️ NEWEST ONLY. Runs accumulate every cycle and a question answered ten
 * minutes ago is still sitting in every older document — folding them together
 * would keep reporting a name somebody has already fixed, which is how a
 * warning surface teaches people to ignore it.
 */
export function useSheetEngineQuestions(facilityId, dateKey) {
  const [questions, setQuestions] = useState([]);
  const [ranAt, setRanAt] = useState(null);
  // ⚠️ A DENIAL IS NOT AN EMPTY LIST. Without this a permission-denied looks
  // exactly like "the sheet matched everybody", which is the more dangerous of
  // the two to show.
  const [denied, setDenied] = useState(false);
  // ⚠️ WHETHER THE SHEET IS STILL REACHING THE BOARD AT ALL — a different fact
  // from "which names it could not resolve", and the one nothing reported. See
  // the refusal note below.
  const [feed, setFeed] = useState(null);

  useEffect(() => {
    if (!facilityId || !dateKey) {
      setQuestions([]);
      setRanAt(null);
      setFeed(null);
      setDenied(false);
      return undefined;
    }
    // No orderBy: `at` is a server timestamp written on every run and an
    // orderBy here would need a composite index for a query that returns a
    // day's worth of small documents anyway. The newest is picked in memory.
    const q = query(
      collection(db, "sheet_engine_runs"),
      where("facilityId", "==", facilityId),
      where("dateKey", "==", dateKey)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setDenied(false);
        let newest = null;
        let newestMs = -1;
        // The newest run that actually LANDED. Two timestamps, because "the
        // engine ran" and "the sheet reached the board" stopped being the same
        // thing the moment a cycle could refuse.
        let lastOkMs = null;
        snap.forEach((d) => {
          const run = d.data();
          const ms = millisOf(run);
          if (ms >= newestMs) { newestMs = ms; newest = run; }
          if (run?.ok === true && ms > 0 && (lastOkMs == null || ms > lastOkMs)) lastOkMs = ms;
        });

        // ⚠️ A REFUSED CYCLE WAS COMPLETELY SILENT, AND THAT IS ITS OWN DEFECT.
        // On 2026-09-14 a cleared `Start` label in the sheet's header row made
        // `detectBoardLayout` refuse the tab 66 cycles in a row; the board went
        // on showing the 1 AM import for five hours and no surface said a word.
        // The freshness gate could not catch it — the ROWS are today's; only
        // their SOURCE died.
        //
        // Published as a feed status rather than folded into `questions`,
        // because they answer different questions and merging them would put a
        // parse failure in a list about people's names.
        setFeed(
          newest
            ? {
                ok: newest.ok === true,
                reason: String(newest.reason || "").trim(),
                atMs: newestMs > 0 ? newestMs : null,
                lastOkMs,
              }
            : null
        );
        // Only a run that actually APPLIED says anything about the board. A
        // refused cycle ("layout not recognised") answered no identity
        // questions at all, and reporting its empty list as "nothing
        // unresolved" would hide the ones the last good run found.
        if (!newest || newest.ok !== true) return;
        setQuestions(questionRows(newest));
        setRanAt(newestMs > 0 ? newestMs : null);
      },
      (err) => {
        console.error("sheet engine questions:", err);
        setDenied(err?.code === "permission-denied");
        setQuestions([]);
        // A listener we cannot read tells us nothing about the feed either;
        // claiming it is healthy would be the more dangerous of the two.
        setFeed(null);
      }
    );
    return unsub;
  }, [facilityId, dateKey]);

  return { questions, ranAt, denied, feed };
}

export default useSheetEngineQuestions;
